<template>
  <div class="step-strategy">
    <header class="strategy-header mb-3 mb-md-4">
      <h2 class="section-title text-subtitle-1 font-weight-medium mb-2">运行策略</h2>
      <p class="strategy-desc text-caption mb-0">
        左侧切换空调、水机与设备运行策略。设备运行策略分为运行策略、参数设置、高级配置；运行策略内切换模式会卸载其它模式组件，避免表单状态串扰。
      </p>
    </header>

    <div class="strategy-body d-flex ga-4 flex-grow-1">
      <aside class="strategy-nav flex-shrink-0">
        <div class="module-tabs-wrap rounded-xl pa-4 h-100">
          <div class="module-tabs d-flex flex-column ga-2">
            <v-tabs v-model="activeSection" direction="vertical" density="comfortable" color="primary">
              <v-tab value="device" prepend-icon="mdi-devices">设备运行策略</v-tab>
              <v-tab value="ac" prepend-icon="mdi-air-conditioner">空调策略</v-tab>
              <v-tab value="water" prepend-icon="mdi-water-pump">水机策略</v-tab>
            </v-tabs>
          </div>
        </div>
      </aside>

      <div class="strategy-main flex-grow-1 min-width-0">
        <div v-show="activeSection === 'ac'" class="section-panel mb-6">
          <AirConditioningPanel ref="acPanelRef" />
        </div>

        <div v-show="activeSection === 'water'" class="section-panel mb-6">
          <WaterChillerPanel ref="waterPanelRef" />
        </div>

        <div v-show="activeSection === 'device'" class="section-panel">
          <div class="device-sub-tabs-wrap rounded-xl pa-2 mb-6">
            <div class="device-sub-tabs d-flex flex-wrap ga-2">
              <v-btn v-for="d in deviceSubTabs" :key="d.key" :variant="activeDeviceSub === d.key ? 'flat' : 'outlined'"
                :color="activeDeviceSub === d.key ? 'primary' : undefined" class="device-sub-tab text-none" rounded="lg"
                @click="activeDeviceSub = d.key">
                {{ d.label }}
              </v-btn>
            </div>
          </div>

          <div v-show="activeDeviceSub === 'run'">
            <div class="run-control-bar rounded-xl pa-4 pa-md-5 mb-6">
              <div
                class="run-control-inner d-flex flex-column flex-md-row align-stretch align-md-center justify-space-between ga-4">
                <div class="run-control-status d-flex align-center ga-3 flex-wrap">
                  <span class="text-body-2 font-weight-medium text-medium-emphasis">策略执行</span>
                  <v-chip :color="strategyRunState === 'running' ? 'success' : 'default'"
                    :variant="strategyRunState === 'running' ? 'flat' : 'tonal'" size="small"
                    class="font-weight-medium">
                    {{ strategyRunState === 'running' ? '运行中' : '已停止' }}
                  </v-chip>
                </div>
                <div class="run-control-actions d-flex flex-wrap ga-3">
                  <v-btn color="success" variant="flat" size="x-large" rounded="lg" min-width="140"
                    class="text-none run-action-btn" prepend-icon="mdi-play-circle"
                    :disabled="strategyRunState === 'running'" @click="onStrategyStart">
                    开始
                  </v-btn>
                  <v-btn color="error" variant="flat" size="x-large" rounded="lg" min-width="140"
                    class="text-none run-action-btn" prepend-icon="mdi-stop-circle"
                    :disabled="strategyRunState === 'stopped'" @click="onStrategyStop">
                    停止
                  </v-btn>
                </div>
              </div>
            </div>

            <div class="mode-tabs-wrap rounded-xl pa-2 mb-6">
              <div class="mode-tabs d-flex flex-wrap ga-2">
                <v-btn v-for="m in modes" :key="m.key" :variant="activeMode === m.key ? 'flat' : 'outlined'"
                  :color="activeMode === m.key ? 'primary' : undefined" class="mode-tab text-none" rounded="lg"
                  @click="activeMode = m.key">
                  {{ m.label }}
                </v-btn>
              </div>
            </div>

            <component :is="panelMap[activeMode]" ref="panelRef" :key="activeMode" />
          </div>

          <DeviceRunParamsPanel v-show="activeDeviceSub === 'params'" ref="paramsPanelRef" />
          <DeviceRunAdvancedPanel v-show="activeDeviceSub === 'advanced'" ref="advancedPanelRef" />
        </div>
      </div>
    </div>

    <div class="strategy-cta d-flex justify-center flex-wrap ga-3">
      <v-btn color="primary" variant="flat" size="large" rounded="lg" min-width="200" @click="onSaveDispatch">
        保存并下发
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick } from 'vue'
import { eui } from 'ems-ui'
// import { AirConditioning, WaterChiller, DeviceRun } from 'lucide-vue-next'

import AirConditioningPanel from './modules/AirConditioningPanel.vue'
import WaterChillerPanel from './modules/WaterChillerPanel.vue'
import PeakShavingPanel from './modes/PeakShavingPanel.vue'
import TransformerPanel from './modes/TransformerPanel.vue'
import VppPanel from './modes/VppPanel.vue'
import AiStormPanel from './modes/AiStormPanel.vue'
import ManualPanel from './modes/ManualPanel.vue'
import DeviceRunParamsPanel from './modes/DeviceRunParamsPanel.vue'
import DeviceRunAdvancedPanel from './modes/DeviceRunAdvancedPanel.vue'

const emit = defineEmits(['update:draft'])

const strategySections = [
  { key: 'device', label: '设备运行策略' },
  { key: 'ac', label: '空调策略' },
  { key: 'water', label: '水机策略' },
]

const activeSection = ref('device')

const modes = [
  { key: 'peak', label: '削峰填谷模式' },
  { key: 'tfm', label: '变压器保护模式' },
  { key: 'vpp', label: 'VPP调度' },
  { key: 'ai', label: 'AI模式' },
  { key: 'manual', label: '手动控制' },
]

const activeMode = ref('peak')

const deviceSubTabs = [
  { key: 'run', label: '运行策略' },
  { key: 'params', label: '参数设置' },
  { key: 'advanced', label: '高级配置' },
]

const activeDeviceSub = ref('run')

/** 运行策略执行状态（静态模拟，未接真实下发） */
const strategyRunState = ref('stopped')

const panelMap = {
  peak: PeakShavingPanel,
  tfm: TransformerPanel,
  vpp: VppPanel,
  ai: AiStormPanel,
  manual: ManualPanel,
}

const acPanelRef = ref(null)
const waterPanelRef = ref(null)
const panelRef = ref(null)
const paramsPanelRef = ref(null)
const advancedPanelRef = ref(null)

function collectDraft() {
  const devicePanel = panelRef.value
  const devicePart = devicePanel?.getDraft?.() ?? {}
  return {
    strategySection: activeSection.value,
    airConditioning: acPanelRef.value?.getDraft?.() ?? {},
    waterChiller: waterPanelRef.value?.getDraft?.() ?? {},
    deviceRun: {
      deviceSub: activeDeviceSub.value,
      strategyRunState: strategyRunState.value,
      strategyTab: activeMode.value,
      ...devicePart,
      paramsSettings: paramsPanelRef.value?.getDraft?.() ?? {},
      advancedConfig: advancedPanelRef.value?.getDraft?.() ?? {},
    },
  }
}

function onSaveDispatch() {
  emit('update:draft', collectDraft())
  eui.showToast('策略草稿已更新（静态模拟，未调用下发接口）', 'success')
}

function onStrategyStart() {
  strategyRunState.value = 'running'
  eui.showToast('策略已标记为运行中（静态模拟，未调用下发接口）', 'success')
}

function onStrategyStop() {
  strategyRunState.value = 'stopped'
  eui.showToast('策略已标记为已停止（静态模拟，未调用下发接口）', 'info')
}

async function getStrategyDraft() {
  await nextTick()
  return collectDraft()
}

defineExpose({
  getStrategyDraft,
})
</script>

<style scoped lang="scss">
/* 与 variables.scss、StationAddStepBasic 表单区、gm-card 阴影对齐 */
.step-strategy {
  --surface-muted: #eef1fb;
  --border-soft: #e9efff;
  --shadow-card: 0 2px 8px rgba(8, 40, 87, 0.05);
  --text-muted: color-mix(in srgb, var(--color-text) 55%, #a0a6bd);
  --strategy-nav-width: 168px;
  min-width: 0;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.strategy-header {
  padding-bottom: 2px;
  border-bottom: 1px solid color-mix(in srgb, var(--color-primary) 10%, var(--border-soft));
}

.section-title {
  color: var(--color-text);
  letter-spacing: -0.01em;
}

.strategy-desc {
  color: var(--text-muted);
  line-height: 1.5;
}

.strategy-nav {
  width: var(--strategy-nav-width);
}

.strategy-nav .module-tabs-wrap {
  position: sticky;
  top: 0;
}

.strategy-body {
  // align-items: flex-start;
  min-height: 0;
}

.strategy-main {
  min-height: 0;
}

.module-tabs-wrap,
.mode-tabs-wrap,
.device-sub-tabs-wrap {
  // background: var(--surface-muted);
  border: 1px solid color-mix(in srgb, var(--color-primary) 8%, var(--border-soft));
  box-shadow: var(--shadow-card);
}

.run-control-bar {
  background: linear-gradient(135deg,
      color-mix(in srgb, var(--color-primary) 6%, var(--surface-muted)) 0%,
      var(--surface-muted) 55%,
      color-mix(in srgb, #2e7d32 5%, var(--surface-muted)) 100%);
  border: 1px solid color-mix(in srgb, var(--color-primary) 14%, var(--border-soft));
  box-shadow: var(--shadow-card);
}

.run-action-btn {
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}

.run-action-btn:not(:disabled):hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
}

.module-tab,
.mode-tab,
.device-sub-tab {
  border-color: color-mix(in srgb, var(--color-primary) 35%, transparent) !important;
  color: var(--color-text);
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.module-tab:not(.v-btn--variant-flat):hover,
.mode-tab:not(.v-btn--variant-flat):hover,
.device-sub-tab:not(.v-btn--variant-flat):hover {
  background: color-mix(in srgb, var(--color-primary) 6%, transparent);
}

.module-tab.v-btn--variant-flat,
.mode-tab.v-btn--variant-flat,
.device-sub-tab.v-btn--variant-flat {
  box-shadow: 0 2px 8px rgba(43, 87, 238, 0.12);
}

.module-tab:focus-visible,
.mode-tab:focus-visible,
.device-sub-tab:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--color-primary) 45%, transparent);
  outline-offset: 2px;
}

.section-panel {
  min-width: 0;
}

.strategy-cta {
  margin-top: 8px;
  padding-top: 24px;
  padding-bottom: 8px;
  border-top: 1px solid var(--border-soft);
}

@media (max-width: 599px) {
  .strategy-body {
    flex-direction: column;
  }

  .strategy-nav {
    width: 100%;
  }

  .strategy-nav .module-tabs-wrap {
    position: static;
  }

  .strategy-nav .module-tabs {
    flex-direction: row !important;
    flex-wrap: wrap;
  }

  .strategy-nav .module-tab {
    flex: 1 1 auto;
    min-width: 0;
  }
}

@media (prefers-reduced-motion: reduce) {

  .module-tab,
  .mode-tab,
  .device-sub-tab {
    transition: none;
  }
}
</style>
